"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { useContext, useState, useEffect, useRef } from "react";
import cx from "classnames";
import SSRIcon from "@ingka/ssr-icon";
import chevronDownSmall from "@ingka/ssr-icon/paths/chevron-down-small";
import { FormFieldContext } from "@ingka/form-field";
import { ListBoxContext } from "@ingka/list-box";
import { getMenuItems, getActions, KeyboardActions } from "./utils/index.mjs";
import Pill from "@ingka/pill";
import crossSmall from "@ingka/ssr-icon/paths/cross-small";
const scrollElementIntoView = (element) => {
  const parentElement = element.parentElement;
  if (parentElement) {
    setTimeout(() => {
      const scrollPositionDiff = parentElement.scrollHeight - parentElement.clientHeight - (parentElement.scrollTop + element.offsetTop + element.clientHeight);
      if (scrollPositionDiff < 0) {
        element.scrollIntoView({ block: "end" });
      }
    }, 0);
  }
};
const updateActiveElement = (activeMenuItemSelector, control, menuItems, index) => {
  menuItems[index].classList.add(activeMenuItemSelector);
  control.setAttribute("aria-activedescendant", menuItems[index].id);
  scrollElementIntoView(menuItems[index]);
};
function getAndResetActiveElement(combobox, prefix, multiple) {
  const menuItems = getMenuItems(
    combobox,
    `.${prefix}menu-item:not(.${prefix}menu-item--disabled)`
  );
  const activeMenuItemClass = `${prefix}menu-item--active`;
  let activeElement = menuItems[0];
  const indexOfActiveElement = menuItems.findIndex(
    (mi) => mi.className.includes(activeMenuItemClass)
  );
  if (indexOfActiveElement > -1) {
    activeElement = menuItems[indexOfActiveElement];
  }
  if (menuItems[indexOfActiveElement] && !multiple) {
    menuItems[indexOfActiveElement].classList.remove(activeMenuItemClass);
  }
  return { activeElement, menuItems, activeMenuItemClass };
}
const ComboboxTrigger = (props) => {
  const {
    prefix = "",
    clearButtonLabel,
    combobox,
    isOpen,
    setIsOpen,
    className,
    disabled,
    listboxId,
    comboboxRef,
    placeholder,
    searchTimeout = 800,
    labelId,
    ...otherProps
  } = props;
  const baseClass = `${prefix}combobox-trigger`;
  const [searchString, setSearchString] = useState("");
  const [singleLetterSearchIndex, setSingleLetterSearchIndex] = useState(0);
  const searchTimer = useRef(null);
  const {
    setSelectedItems,
    selectedItems,
    multiple = false
  } = useContext(ListBoxContext);
  const [internalValue, setInternalValue] = useState(
    !selectedItems ? placeholder : void 0
  );
  const classes = cx(
    `${baseClass}`,
    selectedItems && selectedItems.length === 0 && `${baseClass}--no-selected-items`,
    disabled && `${baseClass}--disabled`,
    className
  );
  const { shouldValidate, valid } = useContext(FormFieldContext);
  const borderBaseClass = `${prefix}validation-border`;
  const borderClasses = cx(
    `${borderBaseClass}`,
    shouldValidate && `${borderBaseClass}--${valid}`,
    disabled && `${borderBaseClass}--disabled`
  );
  useEffect(() => {
    if (disabled && isOpen) {
      setIsOpen(false);
    }
  }, [disabled, isOpen, setIsOpen]);
  useEffect(() => {
    if (isOpen && combobox) {
      const menuItems = getMenuItems(
        combobox,
        `.${prefix}menu-item:not(.${prefix}menu-item--disabled)`
      );
      if (menuItems && menuItems?.length > 0) {
        let selectedItem = menuItems.find(
          (item) => item.getAttribute("aria-selected") === "true"
        );
        if (!selectedItem) {
          selectedItem = menuItems[0];
        }
        scrollElementIntoView(selectedItem);
      }
    }
  }, [isOpen, comboboxRef, combobox, prefix]);
  useEffect(() => {
    if (combobox && combobox.current && selectedItems && selectedItems.length > 0) {
      const activatedItem = combobox.current.querySelector(
        `#${selectedItems[0]}`
      );
      if (activatedItem?.textContent) {
        setInternalValue(activatedItem.textContent);
      }
    }
  }, [combobox, selectedItems]);
  useEffect(() => {
    if (placeholder && selectedItems?.length === 0) {
      setInternalValue(placeholder);
    }
  }, [placeholder, selectedItems]);
  return /* @__PURE__ */ jsxs("div", { className: classes, children: [
    multiple && selectedItems && selectedItems.length > 0 && /* @__PURE__ */ jsx(
      Pill,
      {
        onClick: () => {
          if (setSelectedItems) {
            setSelectedItems([]);
            if (comboboxRef && comboboxRef.current) {
              comboboxRef.current.focus();
            }
          }
        },
        disabled,
        iconPosition: "trailing",
        ssrIcon: crossSmall,
        className: `${baseClass}__pill`,
        prefix,
        label: selectedItems.length,
        "aria-label": clearButtonLabel,
        size: "xsmall",
        selected: true
      }
    ),
    /* @__PURE__ */ jsxs(
      "div",
      {
        ref: comboboxRef,
        "aria-controls": listboxId,
        "aria-expanded": isOpen,
        "aria-haspopup": "listbox",
        "aria-disabled": disabled,
        tabIndex: 0,
        role: "combobox",
        "aria-describedby": labelId,
        className: `${baseClass}__action`,
        onClick: () => {
          const nextIsOpenState = !isOpen;
          setIsOpen(nextIsOpenState);
          if (combobox && nextIsOpenState) {
            const menuItems = getMenuItems(
              combobox,
              `.${prefix}menu-item:not(.${prefix}menu-item--disabled)`
            );
            let selectedItem = menuItems.find(
              (item) => item.getAttribute("aria-selected") === "true"
            );
            if (!selectedItem) {
              selectedItem = menuItems[0];
            }
            if (selectedItem && comboboxRef && comboboxRef.current) {
              comboboxRef.current.setAttribute(
                "aria-activedescendant",
                selectedItem.id
              );
            }
          }
        },
        onKeyDown: (e) => {
          const actions = getActions(e, isOpen, multiple);
          if (actions.length === 0) {
            return;
          }
          actions.forEach((action) => {
            if (action === KeyboardActions.prevent) {
              e.stopPropagation();
              e.preventDefault();
            }
            if (action === KeyboardActions.select) {
              if (combobox && combobox.current) {
                const { activeElement } = getAndResetActiveElement(
                  combobox,
                  prefix,
                  multiple
                );
                activeElement.click();
              }
            }
            if (action === KeyboardActions.open) {
              if (!isOpen) {
                setIsOpen(true);
                if (combobox) {
                  const menuItems = getMenuItems(
                    combobox,
                    `.${prefix}menu-item:not(.${prefix}menu-item--disabled)`
                  );
                  let selectedItem = menuItems.find(
                    (item) => item.getAttribute("aria-selected") === "true"
                  );
                  if (!selectedItem) {
                    selectedItem = menuItems[0];
                  }
                  if (selectedItem && comboboxRef && comboboxRef.current) {
                    comboboxRef.current.setAttribute(
                      "aria-activedescendant",
                      selectedItem.id
                    );
                    if (!actions.includes(KeyboardActions.search)) {
                      selectedItem.classList.add(`${prefix}menu-item--active`);
                    }
                  }
                }
              }
            } else if (action === KeyboardActions.close) {
              if (isOpen) {
                setIsOpen(false);
              }
            }
            if (action === KeyboardActions.search) {
              if (combobox && combobox.current) {
                const nextSearchStringState = searchString + e.key;
                if (searchTimer.current) {
                  clearTimeout(searchTimer.current);
                  searchTimer.current = null;
                }
                const { menuItems, activeMenuItemClass } = getAndResetActiveElement(combobox, prefix, false);
                searchTimer.current = setTimeout(() => {
                  setSearchString("");
                  setSingleLetterSearchIndex(0);
                }, searchTimeout);
                setSearchString(nextSearchStringState);
                if (nextSearchStringState && nextSearchStringState !== "") {
                  let nextSingleLetterIndex = singleLetterSearchIndex;
                  if (searchString && searchString[0] === e.key && searchString[searchString.length - 1] === e.key) {
                    nextSingleLetterIndex += 1;
                  } else {
                    nextSingleLetterIndex = 0;
                  }
                  if (searchString && searchString[0]) {
                    setSingleLetterSearchIndex(nextSingleLetterIndex);
                  }
                  const filtered = menuItems.filter((item) => {
                    const text = item.textContent?.toLowerCase();
                    if (text) {
                      if (nextSingleLetterIndex > 0) {
                        return text.toLowerCase().startsWith(nextSearchStringState[0]);
                      }
                      return text.toLowerCase().startsWith(nextSearchStringState);
                    }
                  });
                  if (comboboxRef && comboboxRef.current && filtered.length > 0) {
                    const searchActiveElementIndex = menuItems.indexOf(
                      filtered[nextSingleLetterIndex % filtered.length]
                    );
                    updateActiveElement(
                      activeMenuItemClass,
                      comboboxRef.current,
                      menuItems,
                      searchActiveElementIndex
                    );
                  }
                }
              }
            }
            if (action === KeyboardActions.navigation) {
              if (combobox && combobox.current && comboboxRef && comboboxRef.current) {
                const { activeElement, menuItems, activeMenuItemClass } = getAndResetActiveElement(combobox, prefix, multiple);
                const indexOfActiveElement = menuItems.indexOf(
                  activeElement
                );
                menuItems[indexOfActiveElement].classList.remove(
                  activeMenuItemClass
                );
                const navIndexStepValue = {
                  ArrowUp: -1,
                  ArrowDown: 1,
                  PageUp: -9,
                  PageDown: 9
                };
                const indexStep = navIndexStepValue[e.key] || 0;
                let newIndex;
                if (e.key === "Home") {
                  newIndex = 0;
                } else if (e.key === "End") {
                  newIndex = menuItems.length - 1;
                } else {
                  newIndex = indexOfActiveElement + indexStep;
                  newIndex = indexStep < 0 ? Math.max(newIndex, 0) : Math.min(newIndex, menuItems.length - 1);
                }
                updateActiveElement(
                  activeMenuItemClass,
                  comboboxRef.current,
                  menuItems,
                  newIndex
                );
              }
            }
          });
        },
        ...otherProps,
        "aria-label": multiple ? placeholder : internalValue,
        children: [
          /* @__PURE__ */ jsx("span", { "aria-hidden": "true", children: multiple ? placeholder : internalValue }),
          /* @__PURE__ */ jsx(
            SSRIcon,
            {
              className: `${baseClass}__icon`,
              prefix,
              paths: chevronDownSmall
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsx("span", { className: borderClasses })
  ] });
};
ComboboxTrigger.displayName = "ComboboxTrigger";
var ComboboxTrigger_default = ComboboxTrigger;
export {
  ComboboxTrigger_default as default
};
